import { type Locale } from '../i18n'

/**
 * Contenu de la page « Fulfillment DNVB & D2C » (profil client, pas service).
 * Même pattern que SERVICE_CONTENT : Record<Locale, FulfillmentContent>,
 * structure vérifiée par content-completeness.test.ts.
 */

export type FulfillmentTabId = 'delivery' | 'packaging' | 'returns' | 'inventory'

export type FulfillmentTab = {
  id: FulfillmentTabId
  label: string
  title: string
  desc: string
  points: string[]
  statValue: string
  statLabel: string
  serviceKey: 'service.shipping' | 'service.pickpack' | 'service.returns' | 'service.storage'
}

export type FulfillmentContent = {
  // SEO
  seoTitle: string
  metaDescription: string
  breadcrumb: string

  // Hero
  badge: string
  titleLead: string
  titleHighlight: string
  titleTail: string
  intro: string
  proofPoints: string[]
  ctaPrimary: string
  ctaSecondary: string

  // Définition citable (GEO)
  defTitle: string
  defText: string

  // Pourquoi pas générique
  whyKicker: string
  whyTitleLead: string
  whyTitleHighlight: string
  whyP1: string
  whyP2: string

  // Section tabbée « l'avantage D2C »
  edgeKicker: string
  edgeTitleLead: string
  edgeTitleHighlight: string
  edgeIntro: string
  tabs: FulfillmentTab[]
  tabCta: string

  // Section dashboard
  dashKicker: string
  dashTitleLead: string
  dashTitleHighlight: string
  dashIntro: string
  dashPoints: { title: string; desc: string }[]
  mock: {
    title: string
    subtitle: string
    periods: string[]
    volumeTitle: string
    volumeSub: string
    returnsTitle: string
    returnsSub: string
    carriersTitle: string
    carriersSub: string
    deliveryTitle: string
    deliverySub: string
    exportCsv: string
    live: string
  }

  // Process (SectorFlowAnimation — exactement 3 étapes)
  processTitle: string
  processIntro: string
  flowSteps: string[]
  flowBadge: string
  flowKicker: string

  // Chiffres
  statsKicker: string
  statsTitleLead: string
  statsTitleHighlight: string
  stats: { value: string; label: string; caption: string }[]

  // Bandeau tarifs
  pricingTitle: string
  pricingText: string
  pricingCta: string

  // FAQ
  faqTitle: string
  faqs: { q: string; a: string }[]

  // CTA final
  ctaBadge: string
  ctaTitleLead: string
  ctaTitleHighlight: string
  ctaCopy: string
  ctaFinalPrimary: string
  ctaFinalSecondary: string
  trust1: string
  trust2: string
  trust3: string
}

export const FULFILLMENT_CONTENT: Record<Locale, FulfillmentContent> = {
  fr: {
    seoTitle: 'Fulfillment DNVB & D2C en Belgique | Yaslan 3PL',
    metaDescription:
      'Yaslan gère la logistique de votre marque D2C : livraison 24-48h, packaging personnalisé, retours simplifiés, stock en temps réel. Devis gratuit.',
    breadcrumb: 'Fulfillment DNVB & D2C',

    badge: 'Fulfillment DNVB & D2C',
    titleLead: 'Fulfillment DNVB & D2C : la logistique qui ',
    titleHighlight: 'grandit avec votre marque',
    titleTail: '.',
    intro:
      'Vous avez construit une marque qui vend en direct à ses clients. Nous expédions à la hauteur : livraison 24-48 h en Europe, colis 100 % à votre image, retours sans friction et stock synchronisé en temps réel — pendant que vous créez.',
    proofPoints: [
      'Cut-off 15 h — expédié le jour même',
      'Taux d’erreur < 0,1 %',
      'Votre marque sur chaque colis, jamais la nôtre',
    ],
    ctaPrimary: 'Demander un devis',
    ctaSecondary: 'Voir nos tarifs',

    defTitle: 'Qu’est-ce que le fulfillment D2C / DNVB ?',
    defText:
      "Le fulfillment D2C/DNVB est le service par lequel un prestataire 3PL comme Yaslan reçoit, stocke, prépare (pick & pack) et expédie les commandes d'une marque qui vend en direct à ses clients, sans distribution traditionnelle. Pour une DNVB (marque digitale verticale), il inclut un emballage à l'image de la marque (branded packaging), une expédition sous 24 à 48 h, un suivi des stocks en temps réel et un tableau de bord accessible en continu.",

    whyKicker: 'Pourquoi une logistique dédiée',
    whyTitleLead: 'Votre logistique ne peut pas être ',
    whyTitleHighlight: 'générique',
    whyP1:
      "Une DNVB ne vend pas un produit, elle vend une expérience. Le déballage est votre première impression physique, un drop mal absorbé casse la dynamique d'un lancement, et chaque retour mal géré devient un avis une étoile. La logistique générique — celle qui traite votre colis comme n'importe quel colis — n'est pas à la hauteur de ce que vous avez construit.",
    whyP2:
      "Notre offre D2C couvre les quatre moments où une marque se joue : la vitesse de livraison, l'emballage, les retours et la visibilité sur le stock. Le tout piloté depuis un dashboard que vous aurez réellement envie d'ouvrir chaque matin.",

    edgeKicker: 'L’avantage D2C',
    edgeTitleLead: 'Ce que votre marque ',
    edgeTitleHighlight: 'gagne avec Yaslan',
    edgeIntro:
      'Quatre piliers, quatre moments de vérité pour votre client — et pour votre marge.',
    tabs: [
      {
        id: 'delivery',
        label: 'Livraison 24-48 h',
        title: 'Livraison 24-48 h en Europe',
        desc:
          'Commandes avant 15 h expédiées le jour même. Vos clients en Belgique, France, aux Pays-Bas, en Allemagne et au Luxembourg sont livrés en 24-48 h — nous comparons 30+ transporteurs à chaque commande pour tenir le délai au meilleur prix.',
        points: [
          'Cut-off 15 h, expédition le jour même',
          '30+ transporteurs comparés automatiquement',
          'Suivi de bout en bout, notifications à votre marque',
        ],
        statValue: '24-48 h',
        statLabel: 'livraison Europe',
        serviceKey: 'service.shipping',
      },
      {
        id: 'packaging',
        label: 'Branded packaging',
        title: 'Un unboxing 100 % à votre marque',
        desc:
          'Cartons personnalisés, papier de soie, flyers, cartes de remerciement, encart promo : votre colis est un support marketing, pas une boîte brune anonyme. Nous préparons chaque commande selon votre gabarit d’emballage.',
        points: [
          'Cartons, papier de soie et inserts à votre image',
          'Encartage promo et cartes personnalisées',
          'Le nom Yaslan n’apparaît nulle part',
        ],
        statValue: '100 %',
        statLabel: 'votre marque',
        serviceKey: 'service.pickpack',
      },
      {
        id: 'returns',
        label: 'Retours sans friction',
        title: 'Des retours qui fidélisent',
        desc:
          'Portail de retours en marque blanche, étiquette générée en deux clics, contrôle et remise en stock dès réception. Un retour bien géré transforme une déception en réachat.',
        points: [
          'Portail marque blanche à vos couleurs',
          'Traitement J+1 après réception',
          'Remboursement selon vos règles, pas les nôtres',
        ],
        statValue: 'J+1',
        statLabel: 'traitement des retours',
        serviceKey: 'service.returns',
      },
      {
        id: 'inventory',
        label: 'Inventaire temps réel',
        title: 'Ne survendez plus jamais',
        desc:
          'Votre stock est synchronisé en temps réel avec Shopify, WooCommerce et vos autres canaux. Une source de vérité unique, des alertes de seuil bas et une précision d’inventaire de 99,9 %.',
        points: [
          'Sync Shopify, WooCommerce et marketplaces',
          'Alertes seuil bas par e-mail et webhook',
          'Précision inventaire 99,9 %',
        ],
        statValue: '99,9 %',
        statLabel: 'précision inventaire',
        serviceKey: 'service.storage',
      },
    ],
    tabCta: 'Découvrir le service',

    dashKicker: 'Votre tour de contrôle',
    dashTitleLead: 'Ce que vous voyez, ',
    dashTitleHighlight: 'chaque matin',
    dashIntro:
      'Chaque client Yaslan pilote sa logistique depuis un dashboard temps réel : expéditions, retours, transporteurs, stock. Voici exactement ce que vous verrez.',
    dashPoints: [
      {
        title: 'Volume d’expéditions',
        desc: 'Colis créés jour par jour, sur 7, 30, 90 jours ou 12 mois.',
      },
      {
        title: 'Taux de retour',
        desc: 'Retours rapportés aux colis expédiés — détectez un problème produit tôt.',
      },
      {
        title: 'Performance transporteurs',
        desc: 'Top transporteurs et taux de livraison, pour arbitrer en connaissance de cause.',
      },
      {
        title: 'Export CSV en un clic',
        desc: 'Vos données vous appartiennent : exportez chaque graphique.',
      },
    ],
    mock: {
      title: 'Analytiques',
      subtitle: 'KPIs globaux et tendances',
      periods: ['7 jours', '30 jours', '90 jours', '12 mois'],
      volumeTitle: 'Volume d’expéditions',
      volumeSub: 'Colis créés sur la période',
      returnsTitle: 'Taux de retour',
      returnsSub: 'Retours + colis expédiés, par période',
      carriersTitle: 'Top transporteurs',
      carriersSub: '5 transporteurs les plus utilisés',
      deliveryTitle: 'Taux de livraison',
      deliverySub: 'Part des colis livrés, par transporteur',
      exportCsv: 'Exporter CSV',
      live: 'Temps réel',
    },

    processTitle: 'Simple pour vous, invisible pour vos clients',
    processIntro:
      'Votre boutique reste le centre de gravité : les commandes arrivent seules, partent le jour même et vos clients ne voient que votre marque.',
    flowSteps: ['Commande synchronisée', 'Pick & pack brandé', 'Expédié en 24-48 h'],
    flowBadge: 'Flux D2C',
    flowKicker: 'De la commande au client final',

    statsKicker: 'Des chiffres, pas des promesses',
    statsTitleLead: 'La performance qui protège ',
    statsTitleHighlight: 'votre réputation',
    stats: [
      {
        value: '24-48 h',
        label: 'Livraison Europe',
        caption: 'Cut-off 15 h : commandé aujourd’hui, expédié aujourd’hui.',
      },
      {
        value: '< 4 h',
        label: 'Préparation de commande',
        caption: 'Pick & pack avec contrôle qualité, emballé à votre image.',
      },
      {
        value: '< 0,1 %',
        label: 'Taux d’erreur',
        caption: 'Vos clients reçoivent le bon produit, dans le bon carton.',
      },
    ],

    pricingTitle: 'Et combien ça coûte ?',
    pricingText:
      'Deux coûts seulement — le transport et le pick & pack. Calculateur en ligne, zéro coût caché.',
    pricingCta: 'Voir les tarifs',

    faqTitle: 'Questions fréquentes des marques D2C',
    faqs: [
      {
        q: 'Qu’est-ce que le fulfillment pour une marque DNVB ou D2C ?',
        a: "C'est l'externalisation complète de votre logistique : réception, stockage, préparation (pick & pack), expédition et retours, opérés par un 3PL. Pour une marque D2C, s'y ajoutent un emballage à votre image, une expédition 24-48 h et un stock synchronisé en temps réel avec votre boutique.",
      },
      {
        q: 'À partir de combien de commandes par mois travaillez-vous avec une marque D2C ?',
        a: "Notre grille tarifaire est pensée à partir de 100 commandes par mois, mais il n'y a pas de minimum contractuel. Vous démarrez ou préparez un lancement ? Parlez-nous de votre projet : on vous oriente honnêtement, même si la meilleure solution du moment n'est pas nous.",
      },
      {
        q: 'Le branded packaging est-il inclus ou en option ?',
        a: "L'utilisation de vos emballages personnalisés est incluse dans le pick & pack : cartons imprimés, papier de soie, flyers, cartes de remerciement. Vos matériaux restent les vôtres ; nous ne facturons aucune majoration pour les utiliser.",
      },
      {
        q: 'Comment fonctionne le dashboard de suivi en temps réel ?',
        a: 'Dès l’onboarding, vous accédez à votre espace client : stock par référence, expéditions, retours, analytics transporteurs et exports CSV. Les données sont synchronisées en continu avec votre boutique — ce que vous voyez est l’état réel de l’entrepôt.',
      },
      {
        q: 'Quels délais de livraison proposez-vous aux marques D2C en Europe ?',
        a: 'Toute commande passée avant 15 h est expédiée le jour même. La livraison prend 24 à 48 h sur nos marchés principaux (Belgique, France, Pays-Bas, Allemagne, Luxembourg) ; Royaume-Uni, Suisse et le reste de l’Europe selon le transporteur choisi.',
      },
      {
        q: 'Comment gérez-vous les retours pour une marque D2C ?',
        a: 'Vos clients passent par un portail de retours à vos couleurs, génèrent leur étiquette en deux clics, et chaque retour est contrôlé puis remis en stock dès le lendemain de sa réception. Les règles — remboursement, échange, contrôle qualité — sont les vôtres.',
      },
      {
        q: 'Combien coûte votre fulfillment D2C ?',
        a: 'Deux coûts seulement : le transport et le pick & pack — dès 6,25 € HTVA par commande, tout compris, en livraison point relais. Pas de frais de réception, pas d’abonnement, pas de coûts cachés. Estimez votre tarif avec le calculateur de notre page Tarifs.',
      },
    ],

    ctaBadge: 'Onboarding 15 min · Sans abonnement',
    ctaTitleLead: 'Votre marque mérite une logistique ',
    ctaTitleHighlight: 'à sa hauteur',
    ctaCopy:
      'Parlez-nous de votre marque, de vos volumes et de vos exigences packaging. On revient vers vous sous 4 h ouvrées avec un plan concret et un devis transparent.',
    ctaFinalPrimary: 'Demander un devis',
    ctaFinalSecondary: 'Réserver un appel de 30 min',
    trust1: 'Réponse sous 4 h ouvrées',
    trust2: 'Intégration Shopify & WooCommerce incluse',
    trust3: 'Sans abonnement ni engagement',
  },

  en: {
    seoTitle: 'D2C & DNVB Fulfilment in Belgium | Yaslan 3PL',
    metaDescription:
      'Yaslan runs fulfilment for D2C and DNVB brands: 24-48h delivery, branded packaging, easy returns, real-time stock dashboard. Free quote.',
    breadcrumb: 'DNVB & D2C fulfilment',

    badge: 'DNVB & D2C fulfilment',
    titleLead: 'D2C & DNVB fulfilment that ',
    titleHighlight: 'grows with your brand',
    titleTail: '.',
    intro:
      'You built a brand that sells direct to its customers. We ship accordingly: 24-48h delivery across Europe, parcels 100% in your image, frictionless returns and stock synced in real time — while you create.',
    proofPoints: [
      '3pm cut-off — shipped same day',
      'Error rate < 0.1%',
      'Your brand on every parcel, never ours',
    ],
    ctaPrimary: 'Request a quote',
    ctaSecondary: 'See our pricing',

    defTitle: 'What is D2C / DNVB fulfilment?',
    defText:
      'D2C/DNVB fulfilment is the service through which a 3PL provider like Yaslan receives, stores, prepares (pick & pack) and ships the orders of a brand that sells directly to its customers, without traditional distribution. For a DNVB (digitally native vertical brand), it includes branded packaging, 24-48h dispatch, real-time inventory tracking and an always-on client dashboard.',

    whyKicker: 'Why dedicated logistics',
    whyTitleLead: 'Your logistics cannot be ',
    whyTitleHighlight: 'generic',
    whyP1:
      'A DNVB does not sell a product — it sells an experience. Unboxing is your first physical impression, a poorly absorbed drop breaks the momentum of a launch, and every mishandled return becomes a one-star review. Generic logistics — the kind that treats your parcel like any parcel — is not worthy of what you have built.',
    whyP2:
      'Our D2C offer covers the four moments where a brand wins or loses: delivery speed, packaging, returns and stock visibility. All steered from a dashboard you will genuinely enjoy opening every morning.',

    edgeKicker: 'The D2C edge',
    edgeTitleLead: 'What your brand ',
    edgeTitleHighlight: 'gains with Yaslan',
    edgeIntro: 'Four pillars, four moments of truth for your customer — and your margin.',
    tabs: [
      {
        id: 'delivery',
        label: '24-48h delivery',
        title: '24-48h delivery across Europe',
        desc:
          'Orders placed before 3pm ship the same day. Your customers in Belgium, France, the Netherlands, Germany and Luxembourg receive in 24-48h — we compare 30+ carriers on every order to hit the deadline at the best price.',
        points: [
          '3pm cut-off, same-day dispatch',
          '30+ carriers compared automatically',
          'End-to-end tracking, notifications in your brand',
        ],
        statValue: '24-48h',
        statLabel: 'delivery in Europe',
        serviceKey: 'service.shipping',
      },
      {
        id: 'packaging',
        label: 'Branded packaging',
        title: 'Unboxing 100% in your brand',
        desc:
          'Custom boxes, tissue paper, flyers, thank-you cards, promo inserts: your parcel is a marketing asset, not an anonymous brown box. We prepare every order to your packaging spec.',
        points: [
          'Boxes, tissue paper and inserts in your image',
          'Promo inserts and personalised cards',
          'The Yaslan name appears nowhere',
        ],
        statValue: '100%',
        statLabel: 'your brand',
        serviceKey: 'service.pickpack',
      },
      {
        id: 'returns',
        label: 'Seamless returns',
        title: 'Returns that build loyalty',
        desc:
          'White-label returns portal, label generated in two clicks, inspection and restocking upon receipt. A well-handled return turns disappointment into a repeat purchase.',
        points: [
          'White-label portal in your colours',
          'Processed next business day after receipt',
          'Refunds by your rules, not ours',
        ],
        statValue: 'D+1',
        statLabel: 'returns processing',
        serviceKey: 'service.returns',
      },
      {
        id: 'inventory',
        label: 'Real-time inventory',
        title: 'Never oversell again',
        desc:
          'Your stock is synced in real time with Shopify, WooCommerce and your other channels. A single source of truth, low-stock alerts and 99.9% inventory accuracy.',
        points: [
          'Shopify, WooCommerce and marketplace sync',
          'Low-stock alerts by e-mail and webhook',
          '99.9% inventory accuracy',
        ],
        statValue: '99.9%',
        statLabel: 'inventory accuracy',
        serviceKey: 'service.storage',
      },
    ],
    tabCta: 'Explore the service',

    dashKicker: 'Your control tower',
    dashTitleLead: 'What you see, ',
    dashTitleHighlight: 'every morning',
    dashIntro:
      'Every Yaslan client steers their logistics from a real-time dashboard: shipments, returns, carriers, stock. Here is exactly what you will see.',
    dashPoints: [
      { title: 'Shipment volume', desc: 'Parcels created day by day, over 7, 30, 90 days or 12 months.' },
      { title: 'Return rate', desc: 'Returns against shipped parcels — catch a product issue early.' },
      { title: 'Carrier performance', desc: 'Top carriers and delivery rates, to arbitrate with real data.' },
      { title: 'One-click CSV export', desc: 'Your data is yours: export any chart.' },
    ],
    mock: {
      title: 'Analytics',
      subtitle: 'Global KPIs and trends',
      periods: ['7 days', '30 days', '90 days', '12 months'],
      volumeTitle: 'Shipment volume',
      volumeSub: 'Parcels created over the period',
      returnsTitle: 'Return rate',
      returnsSub: 'Returns + shipped parcels, per period',
      carriersTitle: 'Top carriers',
      carriersSub: '5 most used carriers',
      deliveryTitle: 'Delivery rate',
      deliverySub: 'Share of parcels delivered, per carrier',
      exportCsv: 'Export CSV',
      live: 'Real-time',
    },

    processTitle: 'Simple for you, invisible to your customers',
    processIntro:
      'Your store stays the centre of gravity: orders flow in on their own, ship the same day, and your customers only ever see your brand.',
    flowSteps: ['Order synced', 'Branded pick & pack', 'Shipped in 24-48h'],
    flowBadge: 'D2C flow',
    flowKicker: 'From order to end customer',

    statsKicker: 'Numbers, not promises',
    statsTitleLead: 'Performance that protects ',
    statsTitleHighlight: 'your reputation',
    stats: [
      {
        value: '24-48h',
        label: 'Delivery in Europe',
        caption: '3pm cut-off: ordered today, shipped today.',
      },
      {
        value: '< 4h',
        label: 'Order preparation',
        caption: 'Pick & pack with quality control, packed in your image.',
      },
      {
        value: '< 0.1%',
        label: 'Error rate',
        caption: 'Your customers get the right product, in the right box.',
      },
    ],

    pricingTitle: 'And how much does it cost?',
    pricingText:
      'Two costs only — shipping and pick & pack. Online calculator, zero hidden fees.',
    pricingCta: 'See pricing',

    faqTitle: 'Frequently asked questions from D2C brands',
    faqs: [
      {
        q: 'What is fulfilment for a DNVB or D2C brand?',
        a: 'It is the full outsourcing of your logistics: receiving, storage, preparation (pick & pack), shipping and returns, operated by a 3PL. For a D2C brand, it adds branded packaging, 24-48h dispatch and stock synced in real time with your store.',
      },
      {
        q: 'From how many monthly orders can Yaslan support a D2C brand?',
        a: 'Our rate card is designed from 100 orders per month, but there is no contractual minimum. Just starting out or preparing a launch? Tell us about your project: we answer honestly, even if the best solution right now is not us.',
      },
      {
        q: 'Is branded packaging included or an add-on?',
        a: 'Using your custom packaging is included in pick & pack: printed boxes, tissue paper, flyers, thank-you cards. Your materials remain yours; we charge no surcharge for using them.',
      },
      {
        q: 'How does the real-time stock dashboard work?',
        a: 'From onboarding, you access your client space: stock per SKU, shipments, returns, carrier analytics and CSV exports. Data is synced continuously with your store — what you see is the real state of the warehouse.',
      },
      {
        q: 'What delivery times do you offer D2C brands across Europe?',
        a: 'Any order placed before 3pm ships the same day. Delivery takes 24 to 48 hours on our core markets (Belgium, France, the Netherlands, Germany, Luxembourg); United Kingdom, Switzerland and the rest of Europe depend on the chosen carrier.',
      },
      {
        q: 'How do you handle returns for a D2C brand?',
        a: 'Your customers use a returns portal in your colours, generate their label in two clicks, and every return is inspected then restocked the day after receipt. The rules — refund, exchange, quality check — are yours.',
      },
      {
        q: 'How much does your D2C fulfilment cost?',
        a: 'Two costs only: shipping and pick & pack — from €6.25 excl. VAT per order, all-in, for pickup-point delivery. No receiving fees, no subscription, no hidden costs. Estimate your rate with the calculator on our pricing page.',
      },
    ],

    ctaBadge: '15-min onboarding · No subscription',
    ctaTitleLead: 'Your brand deserves logistics ',
    ctaTitleHighlight: 'that live up to it',
    ctaCopy:
      'Tell us about your brand, your volumes and your packaging requirements. We come back within 4 business hours with a concrete plan and a transparent quote.',
    ctaFinalPrimary: 'Request a quote',
    ctaFinalSecondary: 'Book a 30-min call',
    trust1: 'Reply within 4 business hours',
    trust2: 'Shopify & WooCommerce integration included',
    trust3: 'No subscription, no commitment',
  },

  nl: {
    seoTitle: 'D2C & DNVB Fulfilment in België | Yaslan 3PL',
    metaDescription:
      'Yaslan verzorgt de fulfilment van je D2C- of DNVB-merk: levering 24-48u, gepersonaliseerde verpakking, vlotte retours, real-time voorraad. Gratis offerte.',
    breadcrumb: 'DNVB & D2C fulfilment',

    badge: 'DNVB & D2C fulfilment',
    titleLead: 'D2C & DNVB fulfilment die ',
    titleHighlight: 'meegroeit met je merk',
    titleTail: '.',
    intro:
      'Je hebt een merk gebouwd dat rechtstreeks aan zijn klanten verkoopt. Wij verzenden op dat niveau: levering in 24-48u in Europa, pakketten 100% in jouw stijl, vlotte retours en voorraad in real time gesynchroniseerd — terwijl jij creëert.',
    proofPoints: [
      'Cut-off 15u — zelfde dag verzonden',
      'Foutmarge < 0,1%',
      'Jouw merk op elk pakket, nooit het onze',
    ],
    ctaPrimary: 'Offerte aanvragen',
    ctaSecondary: 'Bekijk onze tarieven',

    defTitle: 'Wat is D2C / DNVB fulfilment?',
    defText:
      'D2C/DNVB fulfilment is de dienst waarbij een 3PL-partner zoals Yaslan de bestellingen van een merk dat rechtstreeks aan zijn klanten verkoopt ontvangt, opslaat, klaarmaakt (pick & pack) en verzendt, zonder traditionele distributie. Voor een DNVB (digitaal geboren merk) omvat dit gepersonaliseerde verpakking (branded packaging), verzending binnen 24-48u, real-time voorraadopvolging en een dashboard dat altijd beschikbaar is.',

    whyKicker: 'Waarom toegewijde logistiek',
    whyTitleLead: 'Jouw logistiek kan niet ',
    whyTitleHighlight: 'generiek zijn',
    whyP1:
      'Een DNVB verkoopt geen product, maar een ervaring. De unboxing is je eerste fysieke indruk, een slecht opgevangen drop breekt het momentum van een lancering, en elke slecht behandelde retour wordt een één-ster-review. Generieke logistiek — die jouw pakket behandelt als eender welk pakket — doet geen recht aan wat je hebt opgebouwd.',
    whyP2:
      'Ons D2C-aanbod dekt de vier momenten waarop een merk wint of verliest: leversnelheid, verpakking, retours en voorraadzichtbaarheid. Alles aangestuurd vanuit een dashboard dat je elke ochtend met plezier opent.',

    edgeKicker: 'Het D2C-voordeel',
    edgeTitleLead: 'Wat je merk ',
    edgeTitleHighlight: 'wint met Yaslan',
    edgeIntro: 'Vier pijlers, vier momenten van waarheid voor je klant — en je marge.',
    tabs: [
      {
        id: 'delivery',
        label: 'Levering 24-48u',
        title: 'Levering in 24-48u in Europa',
        desc:
          'Bestellingen vóór 15u worden dezelfde dag verzonden. Je klanten in België, Frankrijk, Nederland, Duitsland en Luxemburg ontvangen binnen 24-48u — we vergelijken 30+ vervoerders bij elke bestelling om de deadline te halen aan de beste prijs.',
        points: [
          'Cut-off 15u, verzending dezelfde dag',
          '30+ vervoerders automatisch vergeleken',
          'End-to-end tracking, notificaties in jouw merkstijl',
        ],
        statValue: '24-48u',
        statLabel: 'levering in Europa',
        serviceKey: 'service.shipping',
      },
      {
        id: 'packaging',
        label: 'Branded packaging',
        title: 'Unboxing 100% in jouw merkstijl',
        desc:
          'Gepersonaliseerde dozen, zijdepapier, flyers, bedankkaartjes, promo-inserts: jouw pakket is een marketingkanaal, geen anonieme bruine doos. Wij maken elke bestelling klaar volgens jouw verpakkingsrichtlijnen.',
        points: [
          'Dozen, zijdepapier en inserts in jouw stijl',
          'Promo-inserts en gepersonaliseerde kaartjes',
          'De naam Yaslan verschijnt nergens',
        ],
        statValue: '100%',
        statLabel: 'jouw merk',
        serviceKey: 'service.pickpack',
      },
      {
        id: 'returns',
        label: 'Vlotte retours',
        title: 'Retours die klanten binden',
        desc:
          'White-label retourportaal, etiket in twee klikken, controle en herbevoorrading bij ontvangst. Een goed afgehandelde retour verandert een teleurstelling in een herhaalaankoop.',
        points: [
          'White-label portaal in jouw kleuren',
          'Verwerking de werkdag na ontvangst',
          'Terugbetaling volgens jouw regels, niet de onze',
        ],
        statValue: 'D+1',
        statLabel: 'retourverwerking',
        serviceKey: 'service.returns',
      },
      {
        id: 'inventory',
        label: 'Real-time voorraad',
        title: 'Nooit meer overselling',
        desc:
          'Je voorraad wordt in real time gesynchroniseerd met Shopify, WooCommerce en je andere kanalen. Eén bron van waarheid, meldingen bij lage voorraad en 99,9% voorraadnauwkeurigheid.',
        points: [
          'Sync met Shopify, WooCommerce en marketplaces',
          'Lage-voorraadmeldingen via e-mail en webhook',
          '99,9% voorraadnauwkeurigheid',
        ],
        statValue: '99,9%',
        statLabel: 'voorraadnauwkeurigheid',
        serviceKey: 'service.storage',
      },
    ],
    tabCta: 'Ontdek de dienst',

    dashKicker: 'Jouw controletoren',
    dashTitleLead: 'Wat je ziet, ',
    dashTitleHighlight: 'elke ochtend',
    dashIntro:
      'Elke Yaslan-klant stuurt zijn logistiek vanuit een real-time dashboard: verzendingen, retours, vervoerders, voorraad. Dit is exact wat je te zien krijgt.',
    dashPoints: [
      { title: 'Verzendvolume', desc: 'Aangemaakte pakketten per dag, over 7, 30, 90 dagen of 12 maanden.' },
      { title: 'Retourpercentage', desc: 'Retours tegenover verzonden pakketten — spoor een productprobleem vroeg op.' },
      { title: 'Prestaties vervoerders', desc: 'Topvervoerders en leverpercentages, om te beslissen met echte data.' },
      { title: 'CSV-export in één klik', desc: 'Jouw data is van jou: exporteer elke grafiek.' },
    ],
    mock: {
      title: 'Analytics',
      subtitle: 'Globale KPI’s en trends',
      periods: ['7 dagen', '30 dagen', '90 dagen', '12 maanden'],
      volumeTitle: 'Verzendvolume',
      volumeSub: 'Aangemaakte pakketten in de periode',
      returnsTitle: 'Retourpercentage',
      returnsSub: 'Retours + verzonden pakketten, per periode',
      carriersTitle: 'Topvervoerders',
      carriersSub: '5 meest gebruikte vervoerders',
      deliveryTitle: 'Leverpercentage',
      deliverySub: 'Aandeel geleverde pakketten, per vervoerder',
      exportCsv: 'CSV exporteren',
      live: 'Real-time',
    },

    processTitle: 'Simpel voor jou, onzichtbaar voor je klanten',
    processIntro:
      'Je webshop blijft het zwaartepunt: bestellingen stromen vanzelf binnen, vertrekken dezelfde dag en je klanten zien enkel jouw merk.',
    flowSteps: ['Bestelling gesynchroniseerd', 'Branded pick & pack', 'Verzonden in 24-48u'],
    flowBadge: 'D2C-flow',
    flowKicker: 'Van bestelling tot eindklant',

    statsKicker: 'Cijfers, geen beloftes',
    statsTitleLead: 'Prestaties die je ',
    statsTitleHighlight: 'reputatie beschermen',
    stats: [
      {
        value: '24-48u',
        label: 'Levering in Europa',
        caption: 'Cut-off 15u: vandaag besteld, vandaag verzonden.',
      },
      {
        value: '< 4u',
        label: 'Orderverwerking',
        caption: 'Pick & pack met kwaliteitscontrole, verpakt in jouw stijl.',
      },
      {
        value: '< 0,1%',
        label: 'Foutmarge',
        caption: 'Je klanten krijgen het juiste product, in de juiste doos.',
      },
    ],

    pricingTitle: 'En wat kost dat?',
    pricingText:
      'Slechts twee kosten — transport en pick & pack. Online calculator, nul verborgen kosten.',
    pricingCta: 'Bekijk de tarieven',

    faqTitle: 'Veelgestelde vragen van D2C-merken',
    faqs: [
      {
        q: 'Wat is fulfilment voor een DNVB- of D2C-merk?',
        a: 'Het is de volledige uitbesteding van je logistiek: ontvangst, opslag, verwerking (pick & pack), verzending en retours, uitgevoerd door een 3PL. Voor een D2C-merk komen daar gepersonaliseerde verpakking, verzending in 24-48u en real-time voorraadsynchronisatie met je webshop bij.',
      },
      {
        q: 'Vanaf hoeveel bestellingen per maand kan Yaslan een D2C-merk ondersteunen?',
        a: 'Onze tarieflijst is opgebouwd vanaf 100 bestellingen per maand, maar er is geen contractueel minimum. Start je net of bereid je een lancering voor? Vertel ons over je project: we antwoorden eerlijk, ook als de beste oplossing nu niet bij ons ligt.',
      },
      {
        q: 'Is gepersonaliseerde verpakking inbegrepen of een optie?',
        a: 'Het gebruik van jouw gepersonaliseerde verpakking is inbegrepen in de pick & pack: bedrukte dozen, zijdepapier, flyers, bedankkaartjes. Jouw materialen blijven van jou; wij rekenen geen toeslag aan om ze te gebruiken.',
      },
      {
        q: 'Hoe werkt het real-time voorraaddashboard?',
        a: 'Vanaf de onboarding krijg je toegang tot je klantenzone: voorraad per referentie, verzendingen, retours, vervoerdersanalytics en CSV-exports. De data wordt continu gesynchroniseerd met je webshop — wat je ziet, is de echte toestand van het magazijn.',
      },
      {
        q: 'Welke levertijden bieden jullie D2C-merken in Europa?',
        a: 'Elke bestelling vóór 15u wordt dezelfde dag verzonden. De levering duurt 24 tot 48 uur op onze kernmarkten (België, Frankrijk, Nederland, Duitsland, Luxemburg); het Verenigd Koninkrijk, Zwitserland en de rest van Europa hangen af van de gekozen vervoerder.',
      },
      {
        q: 'Hoe verlopen retours voor een D2C-merk?',
        a: 'Je klanten gebruiken een retourportaal in jouw kleuren, genereren hun etiket in twee klikken, en elke retour wordt gecontroleerd en de dag na ontvangst weer op voorraad gezet. De regels — terugbetaling, omruiling, kwaliteitscontrole — bepaal jij.',
      },
      {
        q: 'Wat kost jullie D2C-fulfilment?',
        a: 'Slechts twee kosten: transport en pick & pack — vanaf € 6,25 excl. btw per bestelling, alles inbegrepen, bij levering aan een afhaalpunt. Geen ontvangstkosten, geen abonnement, geen verborgen kosten. Bereken je tarief met de calculator op onze tarievenpagina.',
      },
    ],

    ctaBadge: 'Onboarding in 15 min · Geen abonnement',
    ctaTitleLead: 'Jouw merk verdient logistiek ',
    ctaTitleHighlight: 'op zijn niveau',
    ctaCopy:
      'Vertel ons over je merk, je volumes en je verpakkingseisen. We komen binnen 4 werkuren terug met een concreet plan en een transparante offerte.',
    ctaFinalPrimary: 'Offerte aanvragen',
    ctaFinalSecondary: 'Boek een gesprek van 30 min',
    trust1: 'Antwoord binnen 4 werkuren',
    trust2: 'Shopify- & WooCommerce-integratie inbegrepen',
    trust3: 'Geen abonnement, geen verbintenis',
  },
}
